import { HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject, isDevMode } from '@angular/core';
import { of, tap } from 'rxjs';
import { HttpCacheService } from '../http-cache-service/http-cache.service';

export interface HttpCacheInterceptorConfig {
  blacklist?: (string | RegExp)[];
  whitelist?: (string | RegExp)[];
  generateCacheKey?: (req: HttpRequest<unknown>) => string;
}

const pathMatches = (patterns: (string | RegExp)[] = [], url: string): boolean => {
  return patterns.some((p) => (typeof p === 'string' ? new RegExp(p.replace(/\*/g, '.*')).test(url) : p.test(url)));
};

// Helper to base64 encode Unicode strings safely
function base64EncodeUnicode(str: string): string {
  const utf8Bytes = new TextEncoder().encode(str);
  // Convert Uint8Array to base64 string
  let binary = '';
  utf8Bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return btoa(binary);
}

const defaultCacheKeyGenerator = (req: HttpRequest<unknown>): string => {
  if (req.method === 'POST') {
    return `${req.urlWithParams}::${base64EncodeUnicode(JSON.stringify(req.body))}`;
  }
  return req.urlWithParams;
};

const log = (msg: string) => {
  // just change the false to true to debug locally. The isDevMode() is to make sure it never goes to prod
  // eslint-disable-next-line no-constant-condition
  if (false && isDevMode()) {
    console.log(`[HTTP Cache] ${msg}`);
  }
};

export function httpCacheInterceptor(config: HttpCacheInterceptorConfig = {}): HttpInterceptorFn {
  const { blacklist = [], whitelist = [], generateCacheKey = defaultCacheKeyGenerator } = config;

  return (req, next) => {
    const cacheService = inject(HttpCacheService);
    const lowerUrl = req.url.toLocaleLowerCase();

    if (pathMatches(blacklist, lowerUrl)) {
      log(`Skipped  (blacklisted): ${req.method} ${req.url}`);
      return next(req);
    }

    const shouldCache = req.method === 'GET' || (req.method === 'POST' && pathMatches(whitelist, lowerUrl));

    if (!shouldCache) {
      log(`Skipped (not cacheable or blacklisted): ${req.method} ${req.url}`);
      return next(req);
    }

    const forceRefresh = req.headers.get('x-force-refresh') === 'true';
    const cacheKey = generateCacheKey(req);

    if (!forceRefresh) {
      const cached = cacheService.get(cacheKey);
      if (cached) {
        log(`Cache hit: ${cacheKey}`);
        return of(cached.clone());
      }
    }

    log(`Fetching from network: ${cacheKey}`);

    return next(req).pipe(
      tap((event) => {
        if (event instanceof HttpResponse) {
          cacheService.set(cacheKey, event.clone());
        }
      }),
    );
  };
}
